/* eslint-disable react-hooks/exhaustive-deps */
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';

import { StatusCode } from '../../api/enumerations';
import { totpEnable, totpSetup } from '../../api/mfa';
import { MfaLoginProps } from '../../api/mfa/types';
import OTPInput from '../../components/OTPInput';
import { IconContentCopyMS, IconQrCodeScannerMS } from '../../constants/icons';
import { Constants } from '../../constants/mfa';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import {
  ButtonBox,
  FilledButton,
  HowToScanner,
  ListItem,
  NewCodeButton,
  OutlinedStyledButton,
  QRCodeContainer,
  SecretBox,
  SectionDialog,
  Span,
  Title,
  TitleItem,
} from './styles';

interface SetupTotpProps {
  token: string;
  onAuthenticated: (payload: MfaLoginProps) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}

/**
 * First login of a user whose role requires TOTP: enroll the authenticator app
 * and exchange the pending token for a full session. There is no way to skip.
 */
export function SetupTotp({
  token,
  onAuthenticated,
  onCancel,
  onError,
}: SetupTotpProps): JSX.Element {
  const [secret, setSecret] = useState('');
  const [otpAuthUrl, setOtpAuthUrl] = useState('');
  const [code, setCode] = useState('');
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [loadingEnable, setLoadingEnable] = useState(false);
  const hasRequestedSetup = useRef(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const handleSetup = useCallback(async (): Promise<void> => {
    setLoadingSetup(true);
    setCode('');

    try {
      const { data, detail } = await totpSetup({ token });

      if (detail.description) throw new Error(detail.description);
      if (detail.status_code !== StatusCode.OK)
        throw new Error(Constants.genericError);

      setSecret(data.secret);
      setOtpAuthUrl(data.otp_auth_url);
    } catch (error) {
      onError(getErrorMessage(error));
    } finally {
      setLoadingSetup(false);
    }
  }, [token, onError]);

  useEffect(() => {
    // Once per screen, and once only: every call mints a new secret and drops
    // the previous one, so the StrictMode double effect would leave the QR on
    // screen showing a secret the API has already thrown away.
    if (hasRequestedSetup.current) return;
    hasRequestedSetup.current = true;
    handleSetup();
  }, []);

  const handleEnable = useCallback(async (): Promise<void> => {
    setLoadingEnable(true);

    try {
      const response = await totpEnable({ code, token });

      if (response.detail.description)
        throw new Error(response.detail.description);
      if (response.detail.status_code !== StatusCode.OK)
        throw new Error(Constants.genericError);

      onAuthenticated(response);
    } catch (error) {
      setCode('');
      onError(getErrorMessage(error));
    } finally {
      setLoadingEnable(false);
    }
  }, [code, token, onAuthenticated, onError]);

  const handleCopySecret = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(secret);
      handleSnackbar(Constants.copied, false);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  return (
    <>
      <Title>{Constants.setupTitle}</Title>
      <SectionDialog>{Constants.setupIntro}</SectionDialog>
      <SectionDialog>{Constants.scanInstruction}</SectionDialog>

      <QRCodeContainer>
        <div>
          {loadingSetup || !otpAuthUrl ? (
            <CircularProgress size={80} />
          ) : (
            <QRCode value={otpAuthUrl} />
          )}
        </div>
      </QRCodeContainer>

      <HowToScanner>
        <TitleItem>
          {IconQrCodeScannerMS} <strong>{Constants.howToScan}</strong>
        </TitleItem>
        <ListItem>{Constants.scanStep1}</ListItem>
        <ListItem>{Constants.scanStep2}</ListItem>
        <ListItem>{Constants.scanStep3}</ListItem>
        <ListItem>{Constants.scanStep4}</ListItem>
      </HowToScanner>

      <Typography fontSize="18px" fontWeight={600} m="16px 0 8px">
        {Constants.manualKey}
      </Typography>
      <SecretBox>
        <span>{secret}</span>
        <IconButton
          color="primary"
          disabled={!secret}
          onClick={handleCopySecret}
        >
          {IconContentCopyMS}
        </IconButton>
      </SecretBox>

      <NewCodeButton
        type="button"
        disabled={loadingSetup}
        onClick={handleSetup}
      >
        {Constants.newQrCode}
      </NewCodeButton>

      <Span>{Constants.codeHint}</Span>
      <OTPInput
        length={Constants.codeLength}
        value={code}
        onChange={setCode}
        disabled={loadingSetup}
      />

      <ButtonBox>
        <FilledButton
          onClick={handleEnable}
          disabled={code.length < Constants.codeLength || loadingEnable}
        >
          {loadingEnable ? (
            <CircularProgress size={24} sx={{ color: '#FFF' }} />
          ) : (
            Constants.validate
          )}
        </FilledButton>
        <OutlinedStyledButton onClick={onCancel}>
          {Constants.cancel}
        </OutlinedStyledButton>
      </ButtonBox>
    </>
  );
}
